import {
  applySnapshot,
  flow,
  getRoot,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { UpdateEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/updateEmailCycleRPC";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import {
  createKeycloakEmailModel,
  KeycloakEmailModel,
} from "../../models/KeycloakEmailModel";
import {
  createEmailContentModel,
  EmailContentModel,
} from "../models/EmailContentModel";
import {
  createSingleTimeScheduleModel,
  SingleTimeScheduleModel,
} from "../models/SingleTimeScheduleModel";
import { RecurringScheduleModel } from "../models/RecurringScheduleModel";
import { ImmediateScheduleModel } from "../models/ImmediateScheduleModel";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useUpdateEmailCycleRPCClientImpl } from "../rpcs/RPC";
import { RootStore } from "../../root/store/RootStore";
import { BoundedNameValidationError } from "../../enums/BoundedNameValidationError";
import { KeycloakEmailValidationError } from "../../enums/KeycloakEmailValidationError";

type UpdateEmailCycleRPCError = Exclude<
  UpdateEmailCycleRPC.RPCError,
  | UpdateEmailCycleRPC.RPCError.DuplicateCycleName
  | UpdateEmailCycleRPC.RPCError.InvalidSenderEmail
  | UpdateEmailCycleRPC.RPCError.DomainNotAllowed
>;
export type UpdateEmailCycleSenderEmailRPCError =
  UpdateEmailCycleRPC.RPCError.DomainNotAllowed;

const areSingleTimeSchedulesEqual = (
  currentScheduleType: Instance<typeof SingleTimeScheduleModel>,
  previousScheduleType: Instance<typeof SingleTimeScheduleModel>,
): boolean =>
  currentScheduleType.date.dateString ===
    previousScheduleType.date.dateString &&
  currentScheduleType.time.hour === previousScheduleType.time.hour &&
  currentScheduleType.time.minute === previousScheduleType.time.minute;

const areRecurringSchedulesEqual = (
  currentScheduleType: Instance<typeof RecurringScheduleModel>,
  previousScheduleType: Instance<typeof RecurringScheduleModel>,
): boolean =>
  currentScheduleType.startDate.dateString ===
    previousScheduleType.startDate.dateString &&
  currentScheduleType.endDate.dateString ===
    previousScheduleType.endDate.dateString &&
  currentScheduleType.timeOfDay.hour === previousScheduleType.timeOfDay.hour &&
  currentScheduleType.timeOfDay.minute ===
    previousScheduleType.timeOfDay.minute &&
  JSON.stringify(currentScheduleType.daysOfWeek.value) ===
    JSON.stringify(previousScheduleType.daysOfWeek.value);

const areImmediateSchedulesEqual = (
  currentScheduleType: Instance<typeof ImmediateScheduleModel>,
  previousScheduleType: Instance<typeof ImmediateScheduleModel>,
): boolean => currentScheduleType.type === previousScheduleType.type;

const getInitialState = (): SnapshotIn<typeof UpdateEmailCycleStore> => ({
  scheduleId: EMPTY_CHARACTER,
  templateId: null,
  cycleName: createBoundedNameModel(),
  senderEmail: createKeycloakEmailModel(),
  senderEmailRPCError: null,
  senderName: createBoundedNameModel(),
  templateName: createBoundedNameModel(),
  emailContent: createEmailContentModel(),
  scheduleType: createSingleTimeScheduleModel(),
  loadingUpdateEmailCycle: false,
  updateEmailCycleRPCError: null,
});

export const UpdateEmailCycleStore = types
  .model("UpdateEmailCycleStore", {
    scheduleId: types.string,
    templateId: types.maybeNull(types.string),
    cycleName: BoundedNameModel,
    senderEmail: KeycloakEmailModel,
    senderEmailRPCError: types.maybeNull(
      types.enumeration("UpdateEmailCycleSenderEmailRPCError", [
        UpdateEmailCycleRPC.RPCError.DomainNotAllowed,
      ]),
    ),
    senderName: BoundedNameModel,
    templateName: BoundedNameModel,
    emailContent: EmailContentModel,
    scheduleType: types.union(
      SingleTimeScheduleModel,
      RecurringScheduleModel,
      ImmediateScheduleModel,
    ),
    loadingUpdateEmailCycle: types.boolean,
    updateEmailCycleRPCError: types.maybeNull(
      types.enumeration(
        "UpdateEmailCycleRPCError",
        Object.values(UpdateEmailCycleRPC.RPCError).filter(
          (rpcError): rpcError is UpdateEmailCycleRPCError =>
            ![
              UpdateEmailCycleRPC.RPCError.DuplicateCycleName,
              UpdateEmailCycleRPC.RPCError.InvalidSenderEmail,
              UpdateEmailCycleRPC.RPCError.DomainNotAllowed,
            ].includes(rpcError),
        ),
      ),
    ),
  })
  .views((store) => ({
    get disableSubmit(): boolean {
      const emailCycleDetailsStore =
        getRoot<typeof RootStore>(store).projectsRootStore.projectDetailsStore
          .emailCycleDetailsStore;
      const isImmediateSchedule =
        store.scheduleType.type === "IMMEDIATE_SCHEDULE";

      if (isImmediateSchedule) {
        return true;
      }

      const isEmptyData =
        !store.cycleName.name ||
        !store.senderEmail.value ||
        !store.senderName.name ||
        !store.emailContent.subject.name ||
        !store.emailContent.body.content ||
        store.scheduleType.isEmptyValues;

      const hasValidationError = !!(
        store.cycleName.validationError ??
        store.senderEmail.validationError ??
        store.senderEmailRPCError ??
        store.senderName.validationError ??
        store.emailContent.subject.validationError ??
        store.emailContent.body.validationError ??
        store.scheduleType.hasValidateError
      );

      const hasSameSchedule =
        store.scheduleType.type === "SINGLE_TIME_SCHEDULE"
          ? emailCycleDetailsStore.scheduleType.type ===
              "SINGLE_TIME_SCHEDULE" &&
            areSingleTimeSchedulesEqual(
              store.scheduleType,
              emailCycleDetailsStore.scheduleType,
            )
          : store.scheduleType.type === "RECURRING_SCHEDULE"
            ? emailCycleDetailsStore.scheduleType.type ===
                "RECURRING_SCHEDULE" &&
              areRecurringSchedulesEqual(
                store.scheduleType,
                emailCycleDetailsStore.scheduleType,
              )
            : emailCycleDetailsStore.scheduleType.type ===
                "IMMEDIATE_SCHEDULE" &&
              areImmediateSchedulesEqual(
                store.scheduleType,
                emailCycleDetailsStore.scheduleType,
              );

      const hasAnyChanges =
        store.cycleName.name !== emailCycleDetailsStore.cycleName.name ||
        store.senderEmail.value !== emailCycleDetailsStore.senderEmail.value ||
        store.senderName.name !== emailCycleDetailsStore.senderName.name ||
        store.emailContent.subject.name !==
          emailCycleDetailsStore.emailContent.subject.name ||
        store.emailContent.body.content !==
          emailCycleDetailsStore.emailContent.body.content ||
        !hasSameSchedule;

      return !!(isEmptyData || hasValidationError || !hasAnyChanges);
    },
  }))
  .actions((store) => ({
    setScheduleId: (scheduleId: string): void => {
      store.scheduleId = scheduleId;
    },
    setSenderEmail: (value: string): void => {
      store.senderEmail.setValue(value);
      store.senderEmailRPCError = null;
    },
    setTemplateId: (templateId: string | null): void => {
      store.templateId = templateId;
    },
    updateEmailCycle: flow(function* () {
      try {
        store.loadingUpdateEmailCycle = true;
        store.updateEmailCycleRPCError = null;
        store.senderEmailRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new UpdateEmailCycleRPC.Request(
          new LeoUUID(store.scheduleId),
          store.cycleName.rpcObject,
          store.senderEmail.rpcObject,
          store.senderName.rpcObject,
          store.emailContent.rpcObject,
          store.scheduleType.rpcObject,
        );

        const result: LeoRPCResult<
          UpdateEmailCycleRPC.Response,
          UpdateEmailCycleRPC.Errors.Errors
        > = yield useUpdateEmailCycleRPCClientImpl(apiClient).execute(request);

        if ("response" in result) return;

        switch (result.error.code) {
          case UpdateEmailCycleRPC.RPCError.DuplicateCycleName:
            store.cycleName.validationError =
              BoundedNameValidationError.DuplicateName;
            break;
          case UpdateEmailCycleRPC.RPCError.InvalidSenderEmail:
            store.senderEmail.validationError =
              KeycloakEmailValidationError.InvalidEmailId;
            break;
          case UpdateEmailCycleRPC.RPCError.DomainNotAllowed:
            store.senderEmailRPCError = result.error.code;
            break;
          case UpdateEmailCycleRPC.RPCError.InvalidScheduleId:
          case UpdateEmailCycleRPC.RPCError.ProjectAlreadyArchived:
          case UpdateEmailCycleRPC.RPCError.InvalidSchedule:
          case UpdateEmailCycleRPC.RPCError.ScheduleInProgress:
            store.updateEmailCycleRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingUpdateEmailCycle = false;
      }
    }),
    resetRPCError: (): void => {
      store.updateEmailCycleRPCError = null;
    },
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createUpdateEmailCycleStore = (): Instance<
  typeof UpdateEmailCycleStore
> => UpdateEmailCycleStore.create(getInitialState());
